import React, { useState } from 'react';
import { Modal, Form, Alert, Input, message } from 'antd';
import { connect } from 'react-redux';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { withUserType } from '../withUserType';
import OWButton from '../ow_button/OWButton';
import SupplierContactFields from '../request_to_add_supplier_form/SupplierContactFields';
import SupplierCoverageFields from '../request_to_add_supplier_form/SupplierCoverageFields';
import SimilarSuppliersPanel, {
	getSimilarSuppliersTitle,
} from '../similar_suppliers_panel/SimilarSuppliersPanel';
import useSupplierDuplicateCheck from '../request_to_add_supplier_form/useSupplierDuplicateCheck';
import {
	buildSupplierPayload,
	getSupplierRequestInitialValues,
} from '../request_to_add_supplier_form/supplierRequestFormUtils';
import { getAllSupplierPrivateNetworkEntities } from '../supplier_private_network_create_form/supplier_private_network_utils';
import {
	approveEntityAddRequest,
	rejectEntityAddRequest,
} from '../../thunks/entity_add_requests_thunks';
import {
	createSupplierPrivateNetworkBulk,
	fetchSuppliersForBuyers,
	inviteSupplierUserFromBuyer,
	supplierPrivateNetworksGetDistinct,
} from '../../thunks/supplier_private_networks_thunks';
import {
	problemTypesHeirarchical,
	problemTypesRestCrudThunks,
} from '../../thunks/problem_types_thunks';
import { fetchLocationsWithRegions, locationsRestCrudThunks } from '../../thunks/locations_thunks';

const CONTACT_STEP = 0;
// Only reachable when the duplicate lookup finds similar suppliers — never by simply advancing.
const MATCHES_STEP = 1;
const COVERAGE_STEP = 2;
const CONTACT_STEP_FIELDS = ['companyName', 'contactEmail', 'contactFirstName', 'contactLastName'];

// Redux collection the reused supplier search dispatches into; we only consume the resolved matches.
const DUPLICATE_SEARCH_COLLECTION = 'approveEntityAddRequestDuplicateSearch';

// Approver-facing materialization form, split into two steps (contact → coverage). Pre-filled
// from the request payload; the approver may override before the supplier is created.
//
// If the approver picks an existing supplier out of the duplicate matches, there is nothing to add —
// so the request is rejected (with a reason, as in the normal reject flow) and that existing supplier
// is assigned to the private network instead, exactly as the private network page's "Yes, this one"
// does: bulk private-network rows for the chosen coverage, then an invite for the contact.
function ApproveEntityAddRequestModal({
	visible,
	record,
	hideModal,
	onSuccess,
	problemTypes,
	locations,
	currentUser,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
	fetchLocations,
	fetchMultipleLocations,
	approveRequest,
	rejectRequest,
	createPrivateNetworks,
	inviteSupplierUser,
	searchSuppliers,
	fetchNetworkMembership,
}) {
	const [form] = Form.useForm();
	const [step, setStep] = useState(CONTACT_STEP);
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(undefined);

	const showMatchesStep = () => setStep(MATCHES_STEP);
	const showCoverageStep = () => setStep(COVERAGE_STEP);

	const {
		matches: duplicateMatches,
		existingSupplierIds,
		checking: checkingDuplicates,
		selectedSupplier,
		checkForDuplicates,
		applyMatchToForm,
		clearSelectedSupplier,
	} = useSupplierDuplicateCheck({
		form,
		searchSuppliers,
		fetchNetworkMembership,
		onMatchesFound: showMatchesStep,
		onProceed: showCoverageStep,
	});

	const selectedSupplierName =
		nullSafeGet('displayName', selectedSupplier) || nullSafeGet('name', selectedSupplier);
	// Already in the private network → the assignment updates its coverage rather than adding it, and
	// no invite goes out (the private network page's existing-supplier path doesn't invite either).
	const selectedSupplierAlreadyInNetwork =
		!!selectedSupplier && existingSupplierIds.includes(String(nullSafeGet('id', selectedSupplier)));

	const handleContinue = () => {
		setErrorMessage(undefined);
		form
			.validateFields(CONTACT_STEP_FIELDS)
			.then(checkForDuplicates)
			.catch(() => {});
	};

	// Adding the supplier as it was requested rather than one of the matches — no facility to link.
	const handleAddAsRequested = () => {
		clearSelectedSupplier();
		showCoverageStep();
	};

	const handleBackToContactStep = () => {
		setErrorMessage(undefined);
		setStep(CONTACT_STEP);
	};

	// The approver can swap the requested details for an existing supplier before adding it.
	const handleSelectSupplierMatch = (supplier) => {
		applyMatchToForm(supplier);
		showCoverageStep();
	};

	const finish = () => {
		setSubmitting(false);
		onSuccess();
		hideModal();
	};

	const failWith = (fallbackMessage) => (error) => {
		setSubmitting(false);
		setErrorMessage(typeof error === 'string' ? error : fallbackMessage);
	};

	/**
	 * Invite the supplier contact to OpenWrench, as the private network page does once a supplier is
	 * assigned. Best effort: the supplier is already in the private network either way, so a failed
	 * invite must not surface as a failed approval.
	 *
	 * The invite is sent on behalf of the requester (it is signed by and copies them), since they are
	 * the vendor's day-to-day contact rather than the approver. `requestedByContact` is only hydrated
	 * for active or invited users, so a deactivated requester falls back to the approver.
	 */
	const sendSupplierInvite = (supplierFacilityId, values) => {
		if (!supplierFacilityId || !values.contactEmail) {
			return Promise.resolve();
		}
		return inviteSupplierUser(
			supplierFacilityId,
			nullSafeGet('requestedByContact.email', record) || nullSafeGet('email', currentUser),
			values.contactEmail,
			values.contactFirstName,
			values.contactLastName
		)
			.then(() => {
				message.success(
					`${values.contactFirstName} ${values.contactLastName} has been sent an invite to join OpenWrench at the email address: ${values.contactEmail}`
				);
			})
			.catch(() => {});
	};

	// No match picked: approve as usual, letting the backend create the supplier from the payload.
	const approveAndInvite = (values) => {
		const payloadOverride = buildSupplierPayload(values);
		return approveRequest(nullSafeGet('id', record), payloadOverride)
			.then((approved) => sendSupplierInvite(nullSafeGet('createdEntityId', approved), values))
			.then(finish)
			.catch(failWith('Something went wrong approving this request.'));
	};

	/**
	 * A match was picked: the supplier already exists, so the request is rejected with the approver's
	 * reason and the existing facility is assigned to the private network instead. The private network
	 * rows are written first — the bulk endpoint is create-or-update, so a retry after a failed reject
	 * is safe, whereas rejecting first would leave a closed request with no supplier assigned.
	 */
	const rejectAndAssignExistingSupplier = (values) => {
		const supplierFacilityId = nullSafeGet('id', selectedSupplier);
		const privateNetworkEntities = getAllSupplierPrivateNetworkEntities({
			...values,
			buyerCompanyId: nullSafeGet('facility.buyerCompanyId', currentUser),
			supplierFacilityId,
		});
		return createPrivateNetworks(privateNetworkEntities)
			.then(() => rejectRequest(nullSafeGet('id', record), values.rejectionReason))
			.then(() => {
				if (selectedSupplierAlreadyInNetwork) {
					message.success(`${selectedSupplierName}'s private network coverage has been updated.`);
					return undefined;
				}
				message.success(`${selectedSupplierName} has been added to your private network.`);
				return sendSupplierInvite(supplierFacilityId, values);
			})
			.then(finish)
			.catch(failWith('Something went wrong adding this supplier to your private network.'));
	};

	const handleAddSupplier = () => {
		setErrorMessage(undefined);
		form
			.validateFields()
			.then((values) => {
				setSubmitting(true);
				return selectedSupplier
					? rejectAndAssignExistingSupplier(values)
					: approveAndInvite(values);
			})
			.catch(() => {});
	};

	const footer = (
		<div className="flex flex-row items-center justify-between">
			{step === MATCHES_STEP ? (
				<OWButton type="default" onClick={handleBackToContactStep}>
					Back
				</OWButton>
			) : (
				<span />
			)}
			<div className="flex flex-row items-center" style={{ gap: '8px' }}>
				<OWButton type="default" onClick={hideModal}>
					Cancel
				</OWButton>
				{step === CONTACT_STEP ? (
					<OWButton
						type="primary"
						htmlType="button"
						onClick={handleContinue}
						loading={checkingDuplicates}
					>
						Continue
					</OWButton>
				) : step === MATCHES_STEP ? (
					<OWButton type="primary" htmlType="button" onClick={handleAddAsRequested}>
						No, Add As Requested
					</OWButton>
				) : (
					<OWButton
						type="primary"
						htmlType="button"
						onClick={handleAddSupplier}
						loading={submitting}
					>
						{selectedSupplier ? 'Reject Request & Add Supplier' : 'Add Supplier'}
					</OWButton>
				)}
			</div>
		</div>
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title="Add Supplier"
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
			footer={footer}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={getSupplierRequestInitialValues(record, [])}
			>
				{errorMessage ? <Alert message={errorMessage} type="error" className="mb-4" /> : null}
				{step === MATCHES_STEP ? (
					<div>
						<div className="h5medium mb-1 text-character-primary">
							{getSimilarSuppliersTitle(duplicateMatches.length)}
						</div>
						<SimilarSuppliersPanel
							suppliers={duplicateMatches}
							existingSupplierIds={existingSupplierIds}
							description="Pick one to add that existing supplier to your private network instead — the request is then rejected as a duplicate. Or carry on with the details that were requested."
							onSelectSupplier={handleSelectSupplierMatch}
						/>
					</div>
				) : null}
				{/* Both steps stay mounted (hidden) so values + "Select All" watches persist. */}
				<div hidden={step !== CONTACT_STEP}>
					<SupplierContactFields />
				</div>
				<div hidden={step !== COVERAGE_STEP}>
					{selectedSupplier ? (
						<Alert
							type="info"
							className="mb-4"
							showIcon
							message={
								selectedSupplierAlreadyInNetwork
									? `${selectedSupplierName} is already in your private network`
									: `${selectedSupplierName} is already on OpenWrench`
							}
							description={
								selectedSupplierAlreadyInNetwork
									? "There is no new supplier to add, so this request will be rejected with the reason below and this supplier's coverage will be updated with what you pick here."
									: 'There is no new supplier to add, so this request will be rejected with the reason below and that supplier will be added to your private network with the coverage you pick here.'
							}
						/>
					) : null}
					<SupplierCoverageFields
						form={form}
						problemTypes={problemTypes}
						fetchProblemTypes={fetchProblemTypes}
						fetchMultipleProblemTypes={fetchMultipleProblemTypes}
						locations={locations}
						fetchLocations={fetchLocations}
						fetchMultipleLocations={fetchMultipleLocations}
					/>
					{selectedSupplier ? (
						<Form.Item
							label="Reason for Rejection"
							name="rejectionReason"
							rules={[{ required: true, message: 'A rejection reason is required.' }]}
						>
							<Input.TextArea
								autoSize={{ minRows: 2 }}
								placeholder={`${selectedSupplierName} is already on OpenWrench and has been added to your private network.`}
							/>
						</Form.Item>
					) : null}
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
	locations: state.locations,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	approveRequest: (id, payloadOverride) =>
		dispatch(approveEntityAddRequest(ownProps.userType)(id, payloadOverride)),
	rejectRequest: (id, reason) => dispatch(rejectEntityAddRequest(ownProps.userType)(id, reason)),
	createPrivateNetworks: (entities) =>
		dispatch(createSupplierPrivateNetworkBulk(ownProps.userType)(entities)),
	inviteSupplierUser: (supplierFacilityId, fromEmail, toEmail, firstName, lastName) =>
		dispatch(
			inviteSupplierUserFromBuyer(ownProps.userType)(
				supplierFacilityId,
				fromEmail,
				toEmail,
				firstName,
				lastName
			)
		),
	searchSuppliers: (name, email) =>
		dispatch(fetchSuppliersForBuyers(ownProps.userType)(name, email)(DUPLICATE_SEARCH_COLLECTION)),
	// Membership check for exactly the matched facilities — no target collection, we only consume the
	// resolved rows. `supplierFacilityIds` is a supported filter on the distinct private-network query.
	fetchNetworkMembership: (supplierFacilityIds) =>
		dispatch(
			supplierPrivateNetworksGetDistinct(ownProps.userType)({}, null, null, null, {
				supplierFacilityIds: supplierFacilityIds.join(','),
			})
		),
	fetchProblemTypes: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			problemTypesHeirarchical(ownProps.userType)(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
	fetchLocations: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			fetchLocationsWithRegions(ownProps.userType)(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunks(ownProps.userType).readMultiple(ids, targetCollectionName)),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(ApproveEntityAddRequestModal)
);
